"use client";

import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "snap-site:theme";
const CHANGE_EVENT = "snap-site:theme-changed";

function applyTheme(theme: Theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

function getStoredTheme(value: string | null): Theme {
  return value === "dark" || value === "light" ? value : "light";
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>("light");
  const [resolved, setResolved] = useState<"light" | "dark">("light");

  useEffect(() => {
    const initial = getStoredTheme(localStorage.getItem(STORAGE_KEY));
    setThemeState(initial);
    setResolved(initial);
    applyTheme(initial);
  }, []);

  // Listen for changes from other tabs/components
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) {
        const val = getStoredTheme(e.newValue);
        setThemeState(val);
        setResolved(val);
        applyTheme(val);
      }
    }
    function onCustom() {
      const val = getStoredTheme(localStorage.getItem(STORAGE_KEY));
      setThemeState(val);
      setResolved(val);
    }
    window.addEventListener("storage", onStorage);
    window.addEventListener(CHANGE_EVENT, onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(CHANGE_EVENT, onCustom);
    };
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    setResolved(next);
    applyTheme(next);
    localStorage.setItem(STORAGE_KEY, next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  const toggle = useCallback(() => {
    setTheme(resolved === "light" ? "dark" : "light");
  }, [resolved, setTheme]);

  return { theme, resolved, setTheme, toggle };
}
